/**
 * TicketProgressCard Component
 *
 * Shows progress toward claiming tickets for each active entitlement tier.
 * Displays different progress visualizations based on tier type.
 *
 * Updated to work with EntitlementProgress from backend.
 */

import { memo } from 'react';
import type { EntitlementProgress } from '../../types/jackpot';
import { useTranslation } from '../../hooks/useTranslation';

interface TicketProgressCardProps {
  entitlement: EntitlementProgress;
  index?: number;
  className?: string;
}

/**
 * Format relative date
 */
function formatRelativeDate(isoDate: string | null): string {
  if (!isoDate) return 'Never';
  const date = new Date(isoDate);
  const now = new Date();
  const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Expired';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `${diffDays} days`;
  if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks`;
  return `${Math.ceil(diffDays / 30)} months`;
}

/**
 * Basic tier progress (7 consecutive daily logins)
 */
const BasicTierProgress = memo(function BasicTierProgress({
  entitlement,
}: {
  entitlement: EntitlementProgress;
}) {
  const { t } = useTranslation();
  const currentStreak = entitlement.current_streak ?? 0;
  const streakRequired = entitlement.streak_required ?? 7;
  const daysRemaining = streakRequired - currentStreak;

  return (
    <div className="space-y-3">
      {/* 7-day progress circles */}
      <div className="flex items-center gap-2">
        {[...Array(streakRequired)].map((_, i) => (
          <div
            key={i}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium
                       transition-all duration-200
                       ${
                         i < currentStreak
                           ? 'bg-amber-500 text-white shadow-amber-500/30 shadow-md'
                           : 'bg-slate-700 text-slate-500'
                       }`}
          >
            {i + 1}
          </div>
        ))}
      </div>

      {/* Progress text */}
      <p className="text-sm text-slate-400">
        {currentStreak}/{streakRequired} {t('jackpot.days', 'days')} -{' '}
        <span className="text-amber-400">
          {daysRemaining} {t('jackpot.moreToGo', 'more to go!')}
        </span>
      </p>

      {/* Warning about streak reset */}
      <p className="text-xs text-red-400/70 flex items-center gap-1">
        <span role="img" aria-label="warning">
          {'\u26A0\uFE0F'}
        </span>{' '}
        {t('jackpot.streakWarning', 'Missing a day resets progress')}
      </p>
    </div>
  );
});

/**
 * Pro tier progress (1 login per week for 4 weeks)
 */
const ProTierProgress = memo(function ProTierProgress({
  entitlement,
}: {
  entitlement: EntitlementProgress;
}) {
  const { t } = useTranslation();
  const weeksCheckedIn = entitlement.weeks_checked_in ?? 0;
  const totalWeeks = 4; // Pro tier requires 4 weeks

  return (
    <div className="space-y-3">
      {/* 4-week progress boxes */}
      <div className="flex items-center gap-2">
        {[...Array(totalWeeks)].map((_, i) => (
          <div
            key={i}
            className={`w-12 h-10 rounded-lg flex items-center justify-center font-medium
                       transition-all duration-200
                       ${
                         i < weeksCheckedIn
                           ? 'bg-amber-500 text-white shadow-amber-500/30 shadow-md'
                           : 'bg-slate-700 text-slate-500'
                       }`}
          >
            W{i + 1}
          </div>
        ))}
      </div>

      {/* Progress text */}
      <p className="text-sm text-slate-400">
        {weeksCheckedIn}/{totalWeeks} {t('jackpot.weeksCompleted', 'weeks completed')}
      </p>

      {/* Tip */}
      <p className="text-xs text-amber-400/70 flex items-center gap-1">
        <span role="img" aria-label="tip">
          {'\u{1F4A1}'}
        </span>{' '}
        {t('jackpot.loginWeeklyTip', 'Login once per week to claim tickets')}
      </p>
    </div>
  );
});

/**
 * Enterprise tier progress (2 tickets/day for 30 days)
 */
const EnterpriseTierProgress = memo(function EnterpriseTierProgress({
  entitlement,
}: {
  entitlement: EntitlementProgress;
}) {
  const { t } = useTranslation();
  const daysLoggedIn = entitlement.days_logged_in ?? 0;
  const ticketsClaimed = entitlement.tickets_claimed;
  const ticketsRemaining = entitlement.tickets_remaining;
  const totalDays = 30; // Enterprise tier window

  const percentage = (daysLoggedIn / totalDays) * 100;

  return (
    <div className="space-y-3">
      {/* Progress bar */}
      <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
        <div
          className="bg-gradient-to-r from-amber-500 to-amber-400 h-3 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Tickets info */}
      <div className="flex justify-between text-sm">
        <span className="text-amber-400">
          {ticketsClaimed} {t('jackpot.ticketsClaimed', 'tickets claimed')}
        </span>
        <span className="text-slate-400">
          {ticketsRemaining} {t('jackpot.potentialRemaining', 'potential remaining')}
        </span>
      </div>

      {/* Days progress */}
      <p className="text-sm text-slate-400">
        {daysLoggedIn}/{totalDays} {t('jackpot.daysLogged', 'days logged')}
      </p>

      {/* Tip */}
      <p className="text-xs text-amber-400/70 flex items-center gap-1">
        <span role="img" aria-label="tip">
          {'\u{1F4A1}'}
        </span>{' '}
        {t('jackpot.loginDailyTip', 'Login daily for +2 tickets!')}
      </p>
    </div>
  );
});

export const TicketProgressCard = memo(function TicketProgressCard({
  entitlement,
  index,
  className = '',
}: TicketProgressCardProps) {
  const { t } = useTranslation();
  const { tier, expires_at, status } = entitlement;

  // Get tier display name
  const tierNames: Record<string, string> = {
    basic: t('jackpot.tierBasic', 'Basic'),
    pro: t('jackpot.tierPro', 'Pro'),
    enterprise: t('jackpot.tierEnterprise', 'Enterprise'),
    free_bonus: t('jackpot.tierFreeBonus', 'Free Bonus'),
  };

  // Get status badge color
  const statusColors: Record<string, string> = {
    active: 'bg-green-500/20 text-green-400',
    completed: 'bg-blue-500/20 text-blue-400',
    expired: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className={`p-4 rounded-lg bg-slate-800/50 border border-slate-700 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-slate-200">
            {tierNames[tier] || tier} {t('jackpot.tier', 'Tier')}
            {index !== undefined && index > 0 && (
              <span className="text-slate-500 text-sm ml-1">#{index + 1}</span>
            )}
          </h4>
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[status] || ''}`}>
            {status}
          </span>
        </div>
        {expires_at && (
          <span className="text-xs text-slate-500">
            {t('jackpot.expires', 'Expires')} {formatRelativeDate(expires_at)}
          </span>
        )}
      </div>

      {/* Tier-specific progress */}
      {tier === 'basic' && <BasicTierProgress entitlement={entitlement} />}
      {tier === 'pro' && <ProTierProgress entitlement={entitlement} />}
      {tier === 'enterprise' && <EnterpriseTierProgress entitlement={entitlement} />}
    </div>
  );
});
